import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import '../../accounting/accounting.css';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { runLoading } from '../../../components/loading';
import { api } from '../../../api/client';
import { useFetchMs } from '../../../hooks/useFetchMs';
import { useGlossary, errMessage, type GlossaryRow } from './systemMasters';
import { GlossaryDetailDialog } from './GlossaryDetailDialog';

interface Props { onHome?: () => void; }

/**
 * Settings ▸ System Management ▸ Glossary — LIMS Glossary_ListView.
 * Read-only list of every glossary field (FieldID / FieldName / Acronym / Definition /
 * TableName / Module / BO) with ONLY the Definition column editable inline (LIMS
 * InlineEditMode="Batch"): edits accumulate, then "Save changes" commits them all and
 * "Cancel changes" discards. No New / Delete (AllowNew/AllowDelete=False). Sorted by FieldID.
 */
export function GlossaryPage({ onHome }: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const qc = useQueryClient();
  const { data: rows = [], isFetching, refetch } = useGlossary();
  const loadMs = useFetchMs(isFetching);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // Batch inline edit of Definition: id → new value (only rows whose Definition changed).
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [editRow, setEditRow] = useState<GlossaryRow | null>(null);   // Edit pencil → LIMS Glossary detail form
  const cardRef = useRef<HTMLDivElement>(null);   // detail dialog maximizes to fill this card (LIMS parity)

  const orig = useMemo(() => {
    const m: Record<string, string> = {};
    for (const r of rows) m[r.id] = r.definition ?? '';
    return m;
  }, [rows]);

  const setDef = (id: string, value: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      if (value === (orig[id] ?? '')) delete next[id];   // back to original ⇒ no longer dirty
      else next[id] = value;
      return next;
    });
  };
  const dirtyCount = Object.keys(edits).length;

  const saveChanges = async () => {
    if (dirtyCount === 0) return;
    // Shared confirm-before-save + "Saved successfully." after (standard dialog
    // format, 2026-07-03 — the batch save was previously silent).
    if (!await confirm({ title: 'Save', message: 'Do you want to Save?' })) return;
    setSaving(true);
    try {
      await runLoading((async () => {
        for (const [id, definition] of Object.entries(edits)) {
          await api.put(`/api/v1/system/glossary/${id}/definition`, { definition: definition.trim() ? definition : null });
        }
      })(), { blocking: true, label: 'Saving changes…' });
      setEdits({});
      await qc.invalidateQueries({ queryKey: ['sm-glossary'] });
      notify.success('Saved successfully.');
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSaving(false); }
  };
  const cancelChanges = async () => {
    if (dirtyCount === 0) return;
    if (!await confirm({ title: 'Cancel changes', message: `Discard ${dirtyCount} unsaved Definition change${dirtyCount === 1 ? '' : 's'}?` })) return;
    setEdits({});
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.fieldId, r.fieldName, r.acronym, r.definition, r.tableName, r.module, r.bo]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const txt = (v: string | null | undefined) => v ?? '';
  // LIMS shows the sort affordance ONLY on FieldID + FieldName (Code / Term) — those two are sortable;
  // Acronym / Definition / TableName / Module / BO are filter-only. Default sort = FieldID asc (Code SortOrder=Ascending).
  const columns: AppColumn<GlossaryRow>[] = [
    { field: 'edit', header: '', sortable: false, filterable: false, width: 40, align: 'center',
      render: (r) => <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); setEditRow(r); }}><i className="ti ti-edit" /></button> },
    { field: 'fieldId', header: 'FieldID', width: '8%', minWidth: 60, text: (r) => txt(r.fieldId),
      compare: (a, b) => txt(a.fieldId).localeCompare(txt(b.fieldId), undefined, { numeric: true }) },
    { field: 'fieldName', header: 'FieldName', width: '15%', minWidth: 130, text: (r) => txt(r.fieldName) },
    { field: 'acronym', header: 'Acronym', width: '13%', minWidth: 100, sortable: false, text: (r) => txt(r.acronym) },
    {
      field: 'definition', header: 'Definition', width: '17%', minWidth: 150, sortable: false, text: (r) => txt(r.definition),
      // The ONLY editable column (LIMS batch edit). Always-on input that reads like a cell until focused.
      // Empty Definition shows a blank cell (LIMS parity) — no placeholder dash.
      render: (r) => (
        <input className={`gloss-def-input${edits[r.id] !== undefined ? ' dirty' : ''}`}
          value={edits[r.id] ?? r.definition ?? ''}
          onChange={(e) => setDef(r.id, e.target.value)} onClick={(e) => e.stopPropagation()} />
      ),
    },
    { field: 'tableName', header: 'TableName', width: '15%', minWidth: 120, sortable: false, text: (r) => txt(r.tableName) },
    { field: 'module', header: 'Module', width: '14%', minWidth: 110, sortable: false, text: (r) => txt(r.module) },
    { field: 'bo', header: 'BO', width: '18%', minWidth: 140, sortable: false, text: (r) => txt(r.bo) },
  ];

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" /><span>System Management</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Glossary</span>
        </div>
        {/* BO UI Standard v2: FormTitle + subtitle ON the grey page — never inside the card. */}
        <FormTitle pageId="sm-glossary" loadMs={loadMs ?? null} loading={isFetching} auditFormName="Glossary" />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="acc-body">
        {/* Action toolbar — its OWN full-width row (BO UI Standard §6): form-specific
            Save/Cancel changes in the left slot, then Edit · Refresh; Export far right. */}
        <AppToolbar
          left={dirtyCount > 0 ? (
            <>
              <button className="ca-btn gloss-save" title="Save changes" onClick={() => void saveChanges()} disabled={saving}>
                <span className="tb-i">💾</span> Save changes{dirtyCount > 1 ? ` (${dirtyCount})` : ''}
              </button>
              <button className="ca-btn" title="Cancel changes" onClick={() => void cancelChanges()} disabled={saving}>
                <span className="tb-i">↩︎</span> Cancel changes
              </button>
            </>
          ) : undefined}
          onEdit={() => { const r = rows.find((x) => x.id === [...selectedIds][0]); if (r) setEditRow(r); }}
          editDisabled={selectedIds.size !== 1}
          onRefresh={() => void refetch()} refreshing={isFetching}
          right={
            <span className="app-toolbar__spacer">
              <ExportMenu triggerClassName="ca-btn" filename="Glossary"
                headers={['FieldID', 'FieldName', 'Acronym', 'Definition', 'TableName', 'Module', 'BO']}
                rows={visible.map((r) => [txt(r.fieldId), txt(r.fieldName), txt(r.acronym), edits[r.id] ?? txt(r.definition), txt(r.tableName), txt(r.module), txt(r.bo)])} />
            </span>
          }
        />
        {/* Search lives in its OWN row BELOW the toolbar (BO UI Standard) — never inside the card. */}
        <div className="list-search-row">
          <div className="list-search"><span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
        </div>
        {/* ONE white grid card — grid + its single footer only. */}
        <div className="acc-card" ref={cardRef}>
          <AppDataGrid<GlossaryRow> rows={visible} columns={columns} getRowId={(r) => r.id}
            selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}
            initialSort={{ key: 'fieldId', dir: 'asc' }} loading={isFetching} emptyText="No glossary entries found." />
        </div>
      </div>

      {editRow && <GlossaryDetailDialog row={editRow} cardRef={cardRef} onClose={() => setEditRow(null)} />}
    </div>
  );
}
